/**
 * Hook pour gérer les alertes de modifications de tarifs
 * VERSION CORRIGÉE AVEC COMPARAISON TIMESTAMP COMPLET
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { historiqueService, VerificationResult } from '../services/HistoriqueService';

interface UseTarifsAlertProps {
  dateReference?: string | null;
  onAlert?: () => void;
  enabled?: boolean;
}

interface UseTarifsAlertReturn {
  hasModifications: boolean;
  modifications: any[];
  lastModificationDate: string | null;
  isLoading: boolean;
  error: string | null;
  checkModifications: () => Promise<void>;
  resetAlert: () => void;
}

// Fonction utilitaire pour normaliser les dates - CONSERVE LE TIMESTAMP COMPLET
const normalizeDate = (date: string | null): string | null => {
  if (!date) return null;
  
  // Si c'est déjà un timestamp ISO complet, le retourner tel quel
  if (date.includes('T') && (date.includes('Z') || date.includes('+') || date.includes('-'))) {
    return date;
  }
  
  // Si c'est un timestamp sans timezone, ajouter Z
  if (date.includes('T') && !date.includes('Z') && !date.includes('+') && !date.includes('-')) {
    return date + 'Z';
  }
  
  // Si c'est seulement une date YYYY-MM-DD, ajouter l'heure de début de journée
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date + 'T00:00:00.000Z';
  }
  
  // Essayer de parser et reformater en timestamp complet
  try {
    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate.toISOString();
    }
  } catch (error) {
    console.warn('Impossible de parser la date:', date);
  }
  
  return date;
};

export const useTarifsAlert = ({
  dateReference,
  onAlert,
  enabled = true
}: UseTarifsAlertProps): UseTarifsAlertReturn => {
  const [hasModifications, setHasModifications] = useState(false);
  const [modifications, setModifications] = useState<any[]>([]);
  const [lastModificationDate, setLastModificationDate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Utiliser useRef pour éviter les re-renders inutiles
  const lastCheckedDateRef = useRef<string | null>(null);
  const isCheckingRef = useRef<boolean>(false);

  const resetAlert = useCallback(() => {

    setHasModifications(false);
    setModifications([]);
    setLastModificationDate(null);
    setError(null);
    lastCheckedDateRef.current = null;
    isCheckingRef.current = false;
  }, []);

  const checkModifications = useCallback(async () => {
    if (!dateReference || !enabled) {

      return;
    }

    // Normaliser la date de référence (conserve le timestamp complet)
    const dateNormalisee = normalizeDate(dateReference || null);
    
    // Éviter de re-vérifier la même date ou si une vérification est en cours
    if (dateNormalisee === lastCheckedDateRef.current || isCheckingRef.current) {

      return;
    }

    if (!dateNormalisee) {
      console.error('❌ Date de référence invalide:', dateReference);
      return;
    }

    // Marquer comme en cours de vérification
    isCheckingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      
      const result: VerificationResult = await historiqueService.verifierModifications(dateNormalisee);
      
      
      setHasModifications(result.hasModifications);
      setModifications(result.modifications || []);
      setLastModificationDate(result.lastModificationDate);
      
      // Marquer cette date comme vérifiée
      lastCheckedDateRef.current = dateNormalisee;

      // Déclencher l'alerte si des modifications sont trouvées
      if (result.hasModifications && onAlert) {

        onAlert();
      }
    } catch (err) {
      console.error('❌ Erreur lors de la vérification des modifications:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      setHasModifications(false);
      setModifications([]);
      setLastModificationDate(null);
    } finally {
      setIsLoading(false);
      isCheckingRef.current = false;
    }
  }, [dateReference, enabled, onAlert]);

  // Vérifier seulement si la date change ET qu'on n'a pas déjà vérifié cette date
  useEffect(() => {
    const dateNormalisee = normalizeDate(dateReference || null);
    
    if (dateNormalisee && enabled && dateNormalisee !== lastCheckedDateRef.current && !isCheckingRef.current) {
      
      // Délai pour éviter les appels multiples rapides
      const timeoutId = setTimeout(() => {
        checkModifications();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [dateReference, enabled, checkModifications]);

  return {
    hasModifications,
    modifications,
    lastModificationDate,
    isLoading,
    error,
    checkModifications,
    resetAlert
  };
};
